/**
 * GLSL helpers shared by the view's shaders.
 *
 * Written in GLSL ES 1.00 style (`attribute` / `varying` / `gl_FragColor`), which
 * is what `THREE.ShaderMaterial` expects by default; three prepends the WebGL2
 * compatibility header itself. No `#version` directive here for that reason.
 *
 * The hashes are Dave Hoskins' well-known integer-free variants: cheap, stable
 * across drivers, and adequate for star jitter and cloud noise. Nothing here is
 * used for anything that must be reproducible on the CPU.
 */

export const GLSL_CONSTANTS = /* glsl */ `
#define TAU 6.283185307179586
#define PI  3.141592653589793
`;

export const GLSL_HASH = /* glsl */ `
float hash11(float p) {
  p = fract(p * 0.1031);
  p *= p + 33.33;
  p *= p + p;
  return fract(p);
}

vec2 hash21(float p) {
  vec3 p3 = fract(vec3(p) * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}

float hash13(vec3 p3) {
  p3 = fract(p3 * 0.1031);
  p3 += dot(p3, p3.zyx + 31.32);
  return fract((p3.x + p3.y) * p3.z);
}
`;

/** Value noise plus a fixed-octave fbm. Constant loop bounds keep drivers happy. */
export const GLSL_NOISE = /* glsl */ `
float noise3(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);

  float n000 = hash13(i + vec3(0.0, 0.0, 0.0));
  float n100 = hash13(i + vec3(1.0, 0.0, 0.0));
  float n010 = hash13(i + vec3(0.0, 1.0, 0.0));
  float n110 = hash13(i + vec3(1.0, 1.0, 0.0));
  float n001 = hash13(i + vec3(0.0, 0.0, 1.0));
  float n101 = hash13(i + vec3(1.0, 0.0, 1.0));
  float n011 = hash13(i + vec3(0.0, 1.0, 1.0));
  float n111 = hash13(i + vec3(1.0, 1.0, 1.0));

  float nx00 = mix(n000, n100, f.x);
  float nx10 = mix(n010, n110, f.x);
  float nx01 = mix(n001, n101, f.x);
  float nx11 = mix(n011, n111, f.x);
  return mix(mix(nx00, nx10, f.y), mix(nx01, nx11, f.y), f.z);
}

float fbm4(vec3 p) {
  float total = 0.0;
  float amplitude = 0.5;
  for (int octave = 0; octave < 4; octave++) {
    total += noise3(p) * amplitude;
    p *= 2.02;
    amplitude *= 0.5;
  }
  return total;
}
`;

/**
 * Polar helpers.
 *
 * `angleDelta` returns the shortest absolute separation between two angles, which
 * is the gate test the simulation will later perform on the CPU. Keeping the same
 * formulation on both sides means the drawn gate and the judged gate cannot drift
 * apart — the "見た目と判定を分離" rule needs them to agree geometrically even
 * though they are computed independently.
 */
export const GLSL_POLAR = /* glsl */ `
float angleDelta(float a, float b) {
  float d = abs(a - b);
  d = mod(d, TAU);
  return min(d, TAU - d);
}

/** Folds an angle into one mirrored wedge, producing n-fold kaleidoscope symmetry. */
float kaleido(float a, float symmetry) {
  float wedge = TAU / symmetry;
  float folded = mod(a, wedge);
  return abs(folded - wedge * 0.5);
}
`;

/** Everything, for shaders that want the lot. */
export const GLSL_COMMON = GLSL_CONSTANTS + GLSL_HASH + GLSL_POLAR;
export const GLSL_COMMON_NOISE = GLSL_CONSTANTS + GLSL_HASH + GLSL_NOISE;
